// pages/superadmin/PartnerOutreach.jsx
// Independent partner-recruitment outreach (6ELA / Wizzyl fixed templates)
// for Tenishja's agency outreach campaigns. Sends via the generic
// partnerships@6ela.io / hello@wizzyl.com sender identities, not tied to
// any CustomerCompany record, since recipients here are external prospects
// (agencies, accountants, other professional-service providers) rather than
// AXO's own scraped leads.
//
// Styling/structure deliberately mirrors QAQueue.jsx / CustomersManagement.jsx
// (dash-root/dash-header shell, dark-mode toggle, qa-tab tabs, btn/table-wrap
// vocabulary) so this reads as the same product rather than a bolted-on page.
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import { Textarea } from "../../arc/components/textarea/textarea";
import { Input } from "../../arc/components/input/input";
import { SortableDataTable } from "../../arc/components/sortable-data-table/sortable-data-table";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash-root.dark .dash-header { box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 2px 20px rgba(0,0,0,0.6); }

  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; }

  /* HEADER, matches QAQueue / CustomersManagement header with back button */
  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07);  position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1560px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: var(--radius-sm); color: rgba(255,255,255,0.85); transition: all var(--t); margin-right: 4px; }
  .btn-back:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; transform: translateX(-2px); }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 13.5px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.38); }
  .dash-header-right { display: flex; align-items: center; gap: 8px; }

  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.78); cursor: pointer; text-decoration: none; transition: all var(--t); }
  .btn-header:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; }

  /* SETTINGS */
  .settings-wrap { position: relative; }
  .settings-panel { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; background: #0d2d5a; border: 1px solid rgba(255,255,255,0.1); border-radius: var(--radius); box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 200; padding: 4px; }
  .settings-panel.open { display: block; }
  .dash-root.dark .settings-panel { background: #1a1a24; border-color: var(--border); }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 12.5px; color: rgba(255,255,255,0.7); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--t); }
  .settings-row:hover { background: rgba(255,255,255,0.07); color: #fff; }

  /* TOGGLE */
  .toggle { position: relative; width: 32px; height: 18px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: rgba(255,255,255,0.2); border-radius: 100px; cursor: pointer; transition: background var(--t); }
  .toggle-track::after { content: ''; position: absolute; width: 14px; height: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; box-shadow: 0 1px 4px rgba(0,0,0,0.25); transition: transform 0.2s var(--ease); }
  .toggle input:checked ~ .toggle-track { background: #34d399; }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN */
  .dash-main { max-width: 900px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }
  .page-lead { font-size: 13px; color: var(--text-muted); margin-top: -8px; }

  /* BUTTONS, shared vocabulary with QAQueue */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: color var(--t), background var(--t), border-color var(--t), box-shadow var(--t), opacity var(--t); }
  .btn:focus-visible { box-shadow: 0 0 0 3px var(--accent-dim); }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-primary { background: var(--accent); color: #fff; }
  .btn-primary:hover:not(:disabled) { background: var(--accent-mid); }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-ghost:hover:not(:disabled) { background: var(--surface-raised); border-color: var(--border-strong); color: var(--text); }

  /* TABS, reuses QAQueue's qa-tab component */
  .qa-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
  .qa-tab { padding: 6px 14px; border-radius: var(--radius-sm); border: 1px solid var(--border); background: var(--surface); font-family: var(--sans); font-size: 12.5px; font-weight: 500; cursor: pointer; color: var(--text-muted); transition: all var(--t); }
  .qa-tab:hover { border-color: var(--border-strong); color: var(--text); }
  .qa-tab.active { background: var(--accent); color: #fff; border-color: var(--accent); }

  /* CARD / PANEL */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .panel-section { padding: 20px 22px; }
  .panel-section + .panel-section { border-top: 1px solid var(--border); }
  .panel-title { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; margin-bottom: 3px; }
  .panel-sub { font-size: 12px; color: var(--text-faint); margin-bottom: 16px; }

  /* TEMPLATE PICKER */
  .template-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
  @media (max-width: 640px) { .template-grid { grid-template-columns: 1fr; } }
  .template-card { text-align: left; border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 16px; background: var(--surface-sub); cursor: pointer; transition: all var(--t); }
  .template-card:hover { border-color: var(--border-strong); }
  .template-card.active { border-color: var(--accent); background: var(--accent-dim); }
  .template-name { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 3px; }
  .template-subject { font-family: var(--mono); font-size: 11px; color: var(--text-faint); }

  .preview-box { background: var(--surface-raised); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 14px 16px; font-size: 12.5px; line-height: 1.65; color: var(--text-muted); max-height: 200px; overflow-y: auto; white-space: pre-wrap; }
  .preview-box b { color: var(--text); }

  /* FORM */
  .field { margin-bottom: 14px; }
  .field-label { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-faint); display: block; margin-bottom: 6px; }
  .field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 640px) { .field-row { grid-template-columns: 1fr; } }
  .input, .textarea { width: 100%; padding: 8px 12px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 13px; outline: none; transition: border-color var(--t), box-shadow var(--t); }
  .input::placeholder, .textarea::placeholder { color: var(--text-faint); }
  .input:focus, .textarea:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 3px var(--accent-dim); }
  .textarea { font-family: var(--mono); font-size: 12px; min-height: 130px; resize: vertical; }
  .field-hint { font-size: 11.5px; color: var(--text-faint); margin-top: 6px; }
  .file-input { font-size: 12.5px; color: var(--text-muted); margin-bottom: 16px; }

  /* STATUS */
  .status-banner { margin-top: 16px; padding: 10px 14px; border-radius: var(--radius-sm); font-size: 12.5px; }
  .status-banner.success { background: var(--green-dim); border: 1px solid var(--green-border); color: var(--green); }
  .status-banner.error { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); }

  /* RESULTS */
  .results-summary { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.04em; color: var(--text-faint); margin-top: 16px; }
  .results-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12.5px; }
  .results-table th { text-align: left; padding: 8px 10px; font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-faint); font-weight: 500; border-bottom: 1px solid var(--border); }
  .results-table td { padding: 8px 10px; border-bottom: 1px solid var(--border); color: var(--text); }
  .results-table tr:last-child td { border-bottom: none; }
  .pill { display: inline-block; padding: 2px 9px; border-radius: 100px; font-size: 11px; font-weight: 600; }
  .pill-ok { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
  .pill-fail { background: var(--red-dim); color: var(--red); border: 1px solid var(--red-border); }

  @media (max-width: 768px) {
    .dash-header-inner { padding: 0 16px; }
    .dash-main { padding: 16px; }
  }
`;

const TEMPLATES = {
  "6ela": {
    label: "6ELA",
    subject: "A new cash flow solution for your clients",
    preview: `Hi [First Name],

As a trusted partner to growing businesses, you likely see firsthand how much damage late payments and aged receivables can do to your clients' cash flow and overall stability.

I'm reaching out from 6ELΔ, a Business Debt Co-Pilot. We are partnering with forward-thinking professional service providers like [Company Name] to help them solve their clients' biggest financial bottleneck automatically...

https://demo.6ela.io/register1

Would you be open to a quick 10-minute call next week?`,
  },
  wizzyl: {
    label: "Wizzyl",
    subject: "A zero-risk web infrastructure for your SME clients",
    preview: `Hi [First Name],

Running a successful company like [Company Name], you likely speak with business owners every month who desperately need a professional digital presence, but simply don't have the capital for a large, upfront custom web build...

By joining our Partner Network, your agency can monetise every lead, eliminate maintenance hassles, and improve campaign performance.

Are you open to a brief 15-minute call next week?`,
  },
};

const emptySingle = { toEmail: "", firstName: "", companyName: "" };

function parsePastedRecipients(raw) {
  // Accepts CSV-ish or tab-separated paste: email, first name, company name
  // (header row optional, a row containing "email" in its first cell is
  // treated as a header and skipped).
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split(/,|\t/).map((part) => part.trim()))
    .filter((parts) => parts[0] && parts[0].toLowerCase() !== "email")
    .map(([toEmail, firstName, companyName]) => ({
      toEmail,
      firstName: firstName || "",
      companyName: companyName || "",
    }));
}

export default function PartnerOutreach() {
  const [templateKey, setTemplateKey] = useState("6ela");
  const [mode, setMode] = useState("single"); // single | bulk
  const [single, setSingle] = useState(emptySingle);
  const [bulkRaw, setBulkRaw] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', message }
  const [bulkSummary, setBulkSummary] = useState(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const fileInputRef = useRef(null);

  const template = TEMPLATES[templateKey];

  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // Close settings panel on outside click, matches QAQueue
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const handleTemplateChange = (key) => {
    setTemplateKey(key);
    setStatus(null);
    setBulkSummary(null);
  };

  const handleSingleSend = async (e) => {
    e.preventDefault();
    if (!single.toEmail) {
      setStatus({ type: "error", message: "Recipient email is required." });
      return;
    }
    setSending(true);
    setStatus(null);
    try {
      await apiService.sendPartnerOutreach(templateKey, single);
      setStatus({
        type: "success",
        message: `${template.label} outreach sent to ${single.toEmail}.`,
      });
      setSingle(emptySingle);
    } catch (err) {
      setStatus({
        type: "error",
        message:
          err.response?.data?.error || "Failed to send the outreach email.",
      });
    } finally {
      setSending(false);
    }
  };

  const parsedBulkRecipients = parsePastedRecipients(bulkRaw);

  const handleBulkSend = async () => {
    if (parsedBulkRecipients.length === 0) {
      setStatus({
        type: "error",
        message: "Paste at least one recipient (email, first name, company).",
      });
      return;
    }
    setSending(true);
    setStatus(null);
    setBulkSummary(null);
    try {
      const res = await apiService.sendPartnerOutreachBulk(
        templateKey,
        parsedBulkRecipients,
      );
      setBulkSummary(res.summary);
      setStatus({
        type: "success",
        message: `Sent ${res.summary.sent} of ${res.summary.totalRequested}, ${res.summary.failed} failed.`,
      });
    } catch (err) {
      setStatus({
        type: "error",
        message:
          err.response?.data?.error || "Failed to send bulk outreach emails.",
      });
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => setBulkRaw(evt.target.result);
    reader.readAsText(file);
  };

  return (
    <div className={`dash-root${darkMode ? " dark" : ""}`}>
      <style>{styles}</style>

      {/* HEADER, matches QAQueue / CustomersManagement header with back button */}
      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-brand">
            <Link
              to="/super-admin"
              className="btn-back"
              title="Back to Dashboard"
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <div className="dash-brand-text">
              <div className="dash-brand-name">Partner Outreach</div>
              <div className="dash-brand-sub">Axo Signal</div>
            </div>
          </div>

          <div className="dash-header-right">
            <div className="settings-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn-header"
                onClick={() => setSettingsOpen((o) => !o)}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Settings
              </button>
              <div className={`settings-panel${settingsOpen ? " open" : ""}`}>
                <div className="settings-row">
                  <span>Dark mode</span>
                  <ThemeSwitch
                    iconOnly
                    theme={darkMode ? "dark" : "light"}
                    onThemeChange={(next, variant, trigger) => applyThemeWithTransition(next, trigger, setDarkMode, variant)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="dash-main">
        <p className="page-lead">
          Fixed-copy partner-recruitment templates for independent outreach to
          agencies and other professional-service providers.
        </p>

        <div className="table-wrap">
          <div className="panel-section">
            <div className="panel-title">Template</div>
            <div className="panel-sub">
              Subject and body are fixed, not editable here.
            </div>
            <div className="template-grid">
              {Object.entries(TEMPLATES).map(([key, t]) => (
                <button
                  key={key}
                  type="button"
                  className={`template-card${templateKey === key ? " active" : ""}`}
                  onClick={() => handleTemplateChange(key)}
                >
                  <div className="template-name">{t.label}</div>
                  <div className="template-subject">"{t.subject}"</div>
                </button>
              ))}
            </div>
            <div className="preview-box">{template.preview}</div>
          </div>

          <div className="panel-section">
            <div className="qa-tabs" style={{ marginBottom: 18 }}>
              <button
                type="button"
                className={`qa-tab${mode === "single" ? " active" : ""}`}
                onClick={() => {
                  setMode("single");
                  setStatus(null);
                }}
              >
                Send one
              </button>
              <button
                type="button"
                className={`qa-tab${mode === "bulk" ? " active" : ""}`}
                onClick={() => {
                  setMode("bulk");
                  setStatus(null);
                }}
              >
                Bulk send
              </button>
            </div>

            {mode === "single" && (
              <form onSubmit={handleSingleSend}>
                <div className="field">
                  <label className="field-label">Recipient email *</label>
                  <Input
                    label="Recipient email"
                    hideLabel
                    className="input"
                    type="email"
                    required
                    value={single.toEmail}
                    onChange={(e) =>
                      setSingle({ ...single, toEmail: e.target.value })
                    }
                    placeholder="jane@agency.com"
                  />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label className="field-label">First name</label>
                    <input
                      className="input"
                      value={single.firstName}
                      onChange={(e) =>
                        setSingle({ ...single, firstName: e.target.value })
                      }
                      placeholder="Jane"
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">Company name</label>
                    <input
                      className="input"
                      value={single.companyName}
                      onChange={(e) =>
                        setSingle({ ...single, companyName: e.target.value })
                      }
                      placeholder="Jane's Agency"
                    />
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={sending}
                >
                  {sending ? "Sending…" : `Send ${template.label} outreach`}
                </button>
              </form>
            )}

            {mode === "bulk" && (
              <div>
                <div className="field">
                  <label className="field-label">
                    Paste recipients, one per line: email, first name, company
                    name
                  </label>
                  <Textarea
                    label="Paste recipients"
                    hideLabel
                    className="textarea"
                    value={bulkRaw}
                    onChange={(e) => setBulkRaw(e.target.value)}
                    placeholder={
                      "jane@agency.com, Jane, Jane's Agency\nmark@studio.io, Mark, Studio Co"
                    }
                  />
                  <div className="field-hint">
                    {parsedBulkRecipients.length} recipient
                    {parsedBulkRecipients.length === 1 ? "" : "s"} detected · up
                    to 100 per send · or upload a CSV below
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  onChange={handleFileUpload}
                  className="file-input"
                />
                <div>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleBulkSend}
                    disabled={sending || parsedBulkRecipients.length === 0}
                  >
                    {sending
                      ? "Sending…"
                      : `Send ${template.label} outreach to ${parsedBulkRecipients.length || ""} recipient${parsedBulkRecipients.length === 1 ? "" : "s"}`}
                  </button>
                </div>
              </div>
            )}

            {status && (
              <div className={`status-banner ${status.type}`}>
                {status.message}
              </div>
            )}

            {bulkSummary && (
              <>
                <div className="results-summary">
                  {bulkSummary.sent} SENT · {bulkSummary.failed} FAILED · OF{" "}
                  {bulkSummary.totalRequested} REQUESTED
                </div>
                <SortableDataTable
                  caption="Bulk send results"
                  rowKey="_key"
                  defaultSort={{ key: "email", direction: "asc" }}
                  rows={bulkSummary.details.map((d, i) => ({
                    ...d,
                    _key: `${d.email}-${i}`,
                  }))}
                  columns={[
                    { key: "email", label: "Email" },
                    {
                      key: "success",
                      label: "Status",
                      sortable: false,
                      render: (value) => (
                        <span
                          className={`pill ${value ? "pill-ok" : "pill-fail"}`}
                        >
                          {value ? "Sent" : "Failed"}
                        </span>
                      ),
                    },
                    {
                      key: "error",
                      label: "Error",
                      sortable: false,
                      render: (value) => (
                        <span style={{ color: "var(--text-faint)" }}>
                          {value || "-"}
                        </span>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
